// IframeIntegrationDocs.js
import { Box, Grid, GridItem, Text, VStack, HStack, Link, Divider, Heading, Code, Container, Button, Spinner } from "@chakra-ui/react";
import { ExternalLinkIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus'; // Adjust the import path based on your project structure
import React, { useState, useContext } from 'react';
import { AppContext } from '../components/AppContext'; // Adjust the import path based on your project structure
import { callApi } from '../callApi'; 
import { UnorderedList, ListItem } from "@chakra-ui/react";

function IframeIntegrationDocs() {
    const { accessToken } = useContext(AppContext);
    const [version, setVersion] = useState([]);
    const [loadingVersion, setLoadingVersion] = useState(false);

  const scrollToSection = (event, sectionId) => {
    event.preventDefault();
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetVersionClick = async () => {
    setLoadingVersion(true);
    try {
      const response = await callApi('/version', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setVersion(data.version);
    } catch (error) {
      console.error("Error fetching version: ", error);
    }
    setLoadingVersion(false);
  };

  return (
    <Box textAlign="center" fontSize="xl">
        <Grid
        templateAreas={`"header header"    
                        "main results"
                        "footer footer"`}
        gridTemplateRows={'60px 1fr 60px'}
        gridTemplateColumns={'350px 4fr'}
        h='100%'
        gap='2'
        color='blackAlpha.800'
        fontWeight='bold'
        >
                {/* Header with menu */}
                    <HeaderMenus />

        {/* Navigation sidebar */}
        <GridItem area="main" bg='white' p={3}>
  <br/>
  
  <VStack textAlign="left" align="start" spacing={3}>
    <Link fontSize="lg" fontWeight="normal" target="_blank" href="/logviewer" 
      color="blue.500">
      View Logs
    </Link>

    {/* Header text saying Iframe Integration Guide */}
    <Heading as="h3" size="m">Iframe Integration Guide</Heading>
    <Divider />
    <Link fontSize="sm" fontWeight="normal" href="#overview">Overview</Link>
    <Link fontSize="sm" fontWeight="normal" href="#prerequisites">Prerequisites</Link>
    <Link fontSize="sm" fontWeight="normal" href="#basic-implementation">Basic Implementation</Link>
    <Link fontSize="sm" fontWeight="normal" href="#auth0-configuration">Auth0 Configuration</Link>
    <Link fontSize="sm" fontWeight="normal" href="#security-headers">Security Headers</Link>
    <Link fontSize="sm" fontWeight="normal" href="#advanced-options">Advanced Options</Link>
    <Link fontSize="sm" fontWeight="normal" href="#troubleshooting">Troubleshooting</Link>
    <Link fontSize="sm" fontWeight="normal" href="#testing-checklist">Testing Checklist</Link>
    <Link fontSize="sm" fontWeight="normal" href="#best-practices">Best Practices</Link>
  </VStack>
  
  </GridItem>

        <GridItem area="results" bg='white' p={6} ml="30px" overflowY="auto" height="700px" width="1000px">
            <VStack spacing={8} textAlign="left" align="start">
            <Box id="overview">
                <Heading as="h2" size="lg" mb={2}>Overview</Heading>
                <Text fontSize="md">
                    This guide explains how to embed Revedy's medical coding platform within your application using iframes. 
                    This allows you to provide a private-labeled experience while leveraging Revedy's powerful AI-driven coding capabilities.
                </Text>
                <Text fontSize="md" mt={2}>
                    <strong>Key Benefits:</strong>
                </Text>
                <UnorderedList fontSize="md" mt={2} ml={6}>
                    <ListItem>Seamless integration into existing workflows</ListItem>
                    <ListItem>Maintain your application's branding and user experience</ListItem>
                    <ListItem>Access to full Revedy functionality including CPT coding, IONM analysis, and medical necessity reviews</ListItem>
                    <ListItem>Secure authentication and data isolation</ListItem>
                </UnorderedList>
                </Box>
                <Divider />

                <Box id="prerequisites">
                    <Heading as="h2" size="lg" mb={2}>Prerequisites</Heading>
                    <Text fontSize="md" mb={2}>
                        Before implementing iframe integration, ensure you have:
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem><strong>SSL Certificate:</strong> Your hosting domain must use HTTPS</ListItem>
                        <ListItem><strong>Auth0 Configuration Access:</strong> Ability to modify Auth0 settings for your organization</ListItem>
                        <ListItem><strong>Server Configuration:</strong> Ability to set HTTP security headers</ListItem>
                        <ListItem><strong>Revedy Account:</strong> Active Revedy subscription with API access</ListItem>
                    </UnorderedList>
                    
                    <Text fontSize="md" mt={4}>
                        <strong>Browser Compatibility:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Chrome 80+ (recommended)</ListItem>
                        <ListItem>Firefox 75+</ListItem>
                        <ListItem>Safari 13.1+</ListItem>
                        <ListItem>Edge 80+</ListItem>
                    </UnorderedList>
                </Box>
                <Divider />

                <Box id="basic-implementation">
                    <Heading as="h2" size="lg" mb={2}>Basic Implementation</Heading>
                    <Text fontSize="md" mb={2}>
                        <strong>Simple Iframe Embedding:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`<!-- Basic iframe implementation -->
<iframe 
  src="https://app.revedy.io/full-cpt-coder"
  width="100%"
  height="800px"
  frameborder="0"
  allow="camera; microphone; clipboard-write"
  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
  title="Revedy Medical Coding Platform">
</iframe>`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Responsive Implementation:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`<div style="position: relative; width: 100%; height: 0; padding-bottom: 75%;">
  <iframe 
    src="https://app.revedy.io/full-cpt-coder"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
    frameborder="0"
    allow="camera; microphone; clipboard-write"
    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
    title="Revedy Medical Coding Platform">
  </iframe>
</div>`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Available Pages for Embedding:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem><Code>/full-cpt-coder</Code> - Main CPT coding interface</ListItem>
                        <ListItem><Code>/scribe</Code> - AI medical scribe functionality</ListItem>
                        <ListItem><Code>/ionmtable</Code> - IONM cases dashboard</ListItem>
                        <ListItem><Code>/medical-necessity-analysis</Code> - Medical necessity analysis</ListItem>
                        <ListItem><Code>/encounters</Code> - Patient encounters list</ListItem>
                    </UnorderedList>
                </Box>
                <Divider />

                <Box id="auth0-configuration">
                    <Heading as="h2" size="lg" mb={2}>Auth0 Configuration</Heading>
                    <Text fontSize="md" mb={2}>
                        <strong>Critical: Third-Party Cookie Considerations</strong>
                    </Text>
                    <Text fontSize="md" mb={2}>
                        Modern browsers block third-party cookies by default, which can break Auth0 authentication in iframes. 
                        Follow these steps to ensure proper authentication:
                    </Text>
                    
                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>1. Configure Allowed Origins in Auth0:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Log into your Auth0 Dashboard</ListItem>
                        <ListItem>Navigate to Applications → Your Revedy Application</ListItem>
                        <ListItem>Add your domain to "Allowed Web Origins"</ListItem>
                        <ListItem>Add your domain to "Allowed Callback URLs"</ListItem>
                    </UnorderedList>

                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`// Example Auth0 Configuration
Allowed Web Origins: https://yourdomain.com
Allowed Callback URLs: https://yourdomain.com/callback
Allowed Logout URLs: https://yourdomain.com`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>2. Custom Domain Setup (Recommended):</strong>
                    </Text>
                    <Text fontSize="md" mb={2}>
                        For the best user experience, configure a custom Auth0 domain that matches your primary domain:
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Set up a custom domain in Auth0 (e.g., auth.yourdomain.com)</ListItem>
                        <ListItem>This eliminates third-party cookie issues</ListItem>
                        <ListItem>Provides seamless authentication experience</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>3. Silent Authentication Setup:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`// Enable silent authentication for iframe contexts
// This should be configured in your Auth0 application settings
{
  "silent_auth": true,
  "web_origins": ["https://yourdomain.com"],
  "cross_origin_auth": true
}`} />
                </Box>
                <Divider />

                <Box id="security-headers">
                    <Heading as="h2" size="lg" mb={2}>Security Headers Configuration</Heading>
                    <Text fontSize="md" mb={2}>
                        <strong>Required Server Configuration:</strong>
                    </Text>
                    <Text fontSize="md" mb={2}>
                        Your server must be configured to allow iframe embedding. Contact Revedy support to configure these headers for your domain:
                    </Text>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>X-Frame-Options Header:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`# Allow your domain to embed Revedy in iframes
X-Frame-Options: ALLOW-FROM https://yourdomain.com

# Or use SAMEORIGIN if hosting on same domain
X-Frame-Options: SAMEORIGIN`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Content Security Policy (CSP):</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`# Example CSP header configuration
Content-Security-Policy: frame-ancestors 'self' https://yourdomain.com;

# For multiple domains
Content-Security-Policy: frame-ancestors 'self' https://yourdomain.com https://app.yourdomain.com;`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Your Website's CSP (if applicable):</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`# Allow embedding Revedy iframes
Content-Security-Policy: frame-src 'self' https://app.revedy.io;`} />
                </Box>
                <Divider />

                <Box id="advanced-options">
                    <Heading as="h2" size="lg" mb={2}>Advanced Implementation Options</Heading>
                    
                    <Text fontSize="md" mb={2}>
                        <strong>Dynamic Height Adjustment:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`<script>
// Listen for height changes from iframe
window.addEventListener('message', function(event) {
  if (event.origin !== 'https://app.revedy.io') return;
  
  if (event.data.type === 'resize') {
    const iframe = document.getElementById('revedy-iframe');
    iframe.style.height = event.data.height + 'px';
  }
});
</script>

<iframe 
  id="revedy-iframe"
  src="https://app.revedy.io/full-cpt-coder"
  width="100%"
  height="800px"
  frameborder="0">
</iframe>`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>URL Parameters for Customization:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`<!-- Hide certain UI elements for embedded view -->
<iframe src="https://app.revedy.io/full-cpt-coder?embedded=true&hide_nav=true"></iframe>

<!-- Pre-select specific case type -->
<iframe src="https://app.revedy.io/ionmtable?case_type=surgery"></iframe>`} />

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Communication Between Parent and Iframe:</strong>
                    </Text>
                    <Code p={2} bg="gray.100" whiteSpace="pre" children=
{`// Listen for events from Revedy iframe
window.addEventListener('message', function(event) {
  if (event.origin !== 'https://app.revedy.io') return;
  
  switch(event.data.type) {
    case 'case_completed':
      console.log('Case completed:', event.data.caseId);
      // Handle case completion in your application
      break;
    case 'user_logout':
      // Handle user logout
      window.location.href = '/logout';
      break;
  }
});`} />
                </Box>
                <Divider />

                <Box id="troubleshooting">
                    <Heading as="h2" size="lg" mb={2}>Troubleshooting Common Issues</Heading>
                    
                    <Text fontSize="md" mb={2}>
                        <strong>Authentication Issues:</strong>
                    </Text>
                    <Container p={2} bg="red.50" borderRadius="md" mt={2}>
                        <Text fontSize="sm"><strong>Problem:</strong> Users see login screen repeatedly</Text>
                        <Text fontSize="sm"><strong>Solution:</strong> Configure Auth0 custom domain or enable cross-origin authentication</Text>
                    </Container>

                    <Container p={2} bg="red.50" borderRadius="md" mt={2}>
                        <Text fontSize="sm"><strong>Problem:</strong> "Refused to display in a frame" error</Text>
                        <Text fontSize="sm"><strong>Solution:</strong> Contact Revedy support to add your domain to allowed frame ancestors</Text>
                    </Container>

                    <Container p={2} bg="red.50" borderRadius="md" mt={2}>
                        <Text fontSize="sm"><strong>Problem:</strong> Iframe appears blank or loads indefinitely</Text>
                        <Text fontSize="sm"><strong>Solution:</strong> Check browser console for CSP violations and verify HTTPS usage</Text>
                    </Container>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Performance Issues:</strong>
                    </Text>
                    <Container p={2} bg="yellow.50" borderRadius="md" mt={2}>
                        <Text fontSize="sm"><strong>Problem:</strong> Slow iframe loading</Text>
                        <Text fontSize="sm"><strong>Solution:</strong> Implement lazy loading and preload critical resources</Text>
                    </Container>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Browser-Specific Issues:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem><strong>Safari:</strong> May require additional SameSite cookie configuration</ListItem>
                        <ListItem><strong>Firefox:</strong> Check Enhanced Tracking Protection settings</ListItem>
                        <ListItem><strong>Chrome:</strong> Ensure third-party cookies are enabled for your domain</ListItem>
                    </UnorderedList>
                </Box>
                <Divider />

                <Box id="testing-checklist">
                    <Heading as="h2" size="lg" mb={2}>Testing Checklist</Heading>
                    <Text fontSize="md" mb={2}>
                        Before deploying your iframe integration, test the following scenarios:
                    </Text>
                    
                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Authentication Testing:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>✓ User can log in successfully within iframe</ListItem>
                        <ListItem>✓ Session persists across page refreshes</ListItem>
                        <ListItem>✓ Logout works properly</ListItem>
                        <ListItem>✓ Session timeout handling</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Functionality Testing:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>✓ File uploads work correctly</ListItem>
                        <ListItem>✓ Audio recording functions (for scribe features)</ListItem>
                        <ListItem>✓ Copy/paste operations work</ListItem>
                        <ListItem>✓ Print functionality (if needed)</ListItem>
                        <ListItem>✓ Download generated reports</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Cross-Browser Testing:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>✓ Chrome (latest version)</ListItem>
                        <ListItem>✓ Firefox (latest version)</ListItem>
                        <ListItem>✓ Safari (latest version)</ListItem>
                        <ListItem>✓ Edge (latest version)</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Mobile Testing:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>✓ iOS Safari</ListItem>
                        <ListItem>✓ Android Chrome</ListItem>
                        <ListItem>✓ Responsive design works in iframe</ListItem>
                        <ListItem>✓ Touch interactions function properly</ListItem>
                    </UnorderedList>
                </Box>
                <Divider />

                <Box id="best-practices">
                    <Heading as="h2" size="lg" mb={2}>Best Practices</Heading>
                    
                    <Text fontSize="md" mb={2}>
                        <strong>Security Best Practices:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Always use HTTPS for both parent and iframe</ListItem>
                        <ListItem>Implement proper CSP headers</ListItem>
                        <ListItem>Validate message origins when using postMessage</ListItem>
                        <ListItem>Regularly review and update Auth0 configurations</ListItem>
                        <ListItem>Monitor for security vulnerabilities</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Performance Best Practices:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Use lazy loading for iframes not immediately visible</ListItem>
                        <ListItem>Set appropriate iframe dimensions to avoid layout shifts</ListItem>
                        <ListItem>Implement loading states for better user experience</ListItem>
                        <ListItem>Consider preloading critical iframe content</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>User Experience Best Practices:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Provide clear loading indicators</ListItem>
                        <ListItem>Handle iframe errors gracefully</ListItem>
                        <ListItem>Ensure consistent styling with your application</ListItem>
                        <ListItem>Test thoroughly on target devices and browsers</ListItem>
                        <ListItem>Provide fallback options if iframe fails to load</ListItem>
                    </UnorderedList>

                    <Text fontSize="md" mt={4} mb={2}>
                        <strong>Support and Maintenance:</strong>
                    </Text>
                    <UnorderedList fontSize="md" mt={2} ml={6}>
                        <ListItem>Contact Revedy support for domain whitelisting</ListItem>
                        <ListItem>Monitor iframe performance and user feedback</ListItem>
                        <ListItem>Keep Auth0 configurations up to date</ListItem>
                        <ListItem>Test after browser updates that might affect iframe behavior</ListItem>
                    </UnorderedList>

                    <Container p={4} bg="blue.50" borderRadius="md" mt={4}>
                        <Text fontSize="md" fontWeight="bold">Need Help?</Text>
                        <Text fontSize="sm" mt={2}>
                            For technical support with iframe integration, contact Revedy support at support@revedy.io 
                            or through your dedicated customer success manager.
                        </Text>
                        <Text fontSize="sm" mt={2}>
                            Include your domain name, Auth0 configuration details, and specific error messages when requesting assistance.
                        </Text>
                    </Container>
                </Box>
                <Divider />

            <Box id="version">
                <Button onClick={handleGetVersionClick} isLoading={loadingVersion}>
                    Get Version
                </Button>
                {version && (
                    <>
                      <Text>Version: {version}</Text>
                    </>
                )}
            </Box>
            <Divider />

            </VStack>
            </GridItem>

        {/* Footer */}
        <GridItem area="footer" bg='white' borderTop='1px' borderColor='gray.200' p={4}>
          {/* Footer content */}
        </GridItem>
      </Grid>
    </Box>
  );
}

export default IframeIntegrationDocs;
